/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';

// Getting all orgs
const requestOrganizationsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedOrganizationsSuccess.create(response);
};

const requestOrganizationsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedOrganizationsError.create(error);
};

export const requestOrganizationsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestOrganizations.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/organizations`).pipe(
          map((response: AjaxResponse<any>): Action => requestOrganizationsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestOrganizationsError(error))),
          ),
        ),
    ),
  );

// Request a specific org
const receivedCurrentOrganizationSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedCurrentOrganizationSuccess.create(response);
};

const receivedCurrentOrganizationError = (error: AjaxError): any => {
  return ActionCreators.ReceivedCurrentOrganizationError.create(error);
};
//
export const requestCurrentOrganizationEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestCurrentOrganization.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/organizations/${$action.payload}`).pipe(
          map((response: AjaxResponse<any>): Action => receivedCurrentOrganizationSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(receivedCurrentOrganizationError(error))),
          ),
        ),
    ),
  );

// Adding an org
const organizationProvisioningSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.OrganizationProvisioningSuccess.create(response);
};

const organizationProvisioningError = (error: AjaxError): any => {
  return ActionCreators.OrganizationProvisioningError.create(error);
};

export const provisionOrganizationEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.ProvisionOrganization.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/provisioning/organizations', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => organizationProvisioningSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(organizationProvisioningError(error))),
          ),
        ),
    ),
  );

// Delete an org
const deleteOrganizationSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.DeleteOrganizationSuccess.create(response);
};

const deleteOrganizationError = (error: AjaxError): any => {
  return ActionCreators.DeleteOrganizationError.create(error);
};

export const deleteOrganizationEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.DeleteOrganization.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.delete(`/provisioning/organizations/${$action.payload}`).pipe(
          switchMap(
            (response: AjaxResponse<any>): Array<Action> => [
              deleteOrganizationSuccess(response),
              ActionCreators.RequestOrganizations.create(),
            ],
          ),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(deleteOrganizationError(error))),
          ),
        ),
    ),
  );

// Update an org
const updateOrganizationSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.UpdateOrganizationSuccess.create(response);
};

const updateOrganizationError = (error: AjaxError): any => {
  return ActionCreators.UpdateOrganizationError.create(error);
};

export const updateOrganizationEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.UpdateOrganization.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.put(`/provisioning/organizations/${$action.payload.id}`).pipe(
          map((response: AjaxResponse<any>): Action => updateOrganizationSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(updateOrganizationError(error))),
          ),
        ),
    ),
  );

// Get terminals for an org
const requestOrganizationTerminalsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedOrganizationTerminalsSuccess.create(response);
};

const requestOrganizationTerminalsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedOrganizationTerminalsError.create(error);
};

export const requestOrganizationTerminalsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestOrganizationTerminals.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/organizations/${$action.payload}/terminals`).pipe(
          map((response: AjaxResponse<any>): Action => requestOrganizationTerminalsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestOrganizationTerminalsError(error))),
          ),
        ),
    ),
  );
